import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useBrandManager } from "@/hooks/useBrandManager";

export interface ReportShare {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  share_type: string;
  share_code: string;
  access_pin: string | null;
  recipient_email: string | null;
  recipient_name: string | null;
  report_config: Record<string, any>;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  schedule?: ReportShareSchedule | null;
}

export interface ReportShareSchedule {
  id: string;
  share_id: string;
  brand_id: string;
  schedule_type: string;
  schedule_time: string;
  schedule_days: number[];
  schedule_day_of_month: number | null;
  timezone: string;
  email_subject: string | null;
  email_template: string | null;
  is_enabled: boolean;
  last_sent_at: string | null;
  next_send_at: string | null;
  created_at: string;
}

export interface CreateShareInput {
  name: string;
  share_type: string;
  access_pin?: string;
  recipient_email?: string;
  recipient_name?: string;
  report_config?: Record<string, any>;
  expires_at?: string;
}

export interface CreateScheduleInput {
  share_id: string;
  schedule_type: string;
  schedule_time?: string;
  schedule_days?: number[];
  schedule_day_of_month?: number;
  timezone?: string;
  email_subject?: string;
  email_template?: string;
  is_enabled?: boolean;
}

const generateShareCode = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Helper to calculate next run time based on schedule
const calculateNextRunAt = (scheduleType: string, scheduleTime: string, scheduleDays?: number[], scheduleDayOfMonth?: number): string => {
  const now = new Date();
  const [hours, minutes] = (scheduleTime || '09:00').split(':').map(Number);
  
  const next = new Date(now);
  next.setHours(hours, minutes, 0, 0);
  
  // If the time has passed today, start from tomorrow
  if (next <= now) {
    next.setDate(next.getDate() + 1);
  }

  switch (scheduleType) {
    case 'daily':
      // Already set to next occurrence
      break;
    case 'weekly':
      const days = scheduleDays || [1];
      let found = false;
      for (let i = 0; i < 7; i++) {
        const checkDate = new Date(next);
        checkDate.setDate(next.getDate() + i);
        if (days.includes(checkDate.getDay())) {
          next.setDate(checkDate.getDate());
          found = true;
          break;
        }
      }
      if (!found) {
        next.setDate(next.getDate() + 7);
      }
      break;
    case 'monthly':
      const dayOfMonth = scheduleDayOfMonth || 1;
      next.setDate(dayOfMonth);
      if (next <= now) {
        next.setMonth(next.getMonth() + 1);
      }
      break;
  }

  return next.toISOString();
};

export const useReportShares = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentBrandId } = useBrandManager();

  const { data: shares, isLoading } = useQuery({
    queryKey: ['report-shares', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];

      // First fetch shares
      const { data: sharesData, error: sharesError } = await supabase
        .from('os_report_shares')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('created_at', { ascending: false });

      if (sharesError) throw sharesError;

      // Then fetch schedules from job_schedules
      const { data: schedules, error: schedulesError } = await supabase
        .from('job_schedules')
        .select('*')
        .eq('brand_id', currentBrandId)
        .eq('job_type', 'report_share');

      if (schedulesError) throw schedulesError;

      // Map schedules to shares by share_id in parameters
      const scheduleMap = new Map<string, any>();
      schedules?.forEach((s: any) => {
        const shareId = s.parameters?.share_id;
        if (shareId) {
          scheduleMap.set(shareId, {
            id: s.id,
            share_id: shareId,
            brand_id: s.brand_id,
            schedule_type: s.parameters?.schedule_type || 'daily',
            schedule_time: s.parameters?.schedule_time || '09:00',
            schedule_days: s.parameters?.schedule_days || [],
            schedule_day_of_month: s.parameters?.schedule_day_of_month || null,
            timezone: s.parameters?.timezone || 'UTC',
            email_subject: s.parameters?.email_subject || null,
            email_template: s.parameters?.email_template || null,
            is_enabled: s.enabled ?? true,
            last_sent_at: s.last_run_at,
            next_send_at: s.next_run_at,
            created_at: s.created_at,
          });
        }
      });

      return (sharesData || []).map(share => ({
        ...share,
        schedule: scheduleMap.get(share.id) || null
      })) as ReportShare[];
    },
    enabled: !!currentBrandId,
  });

  const createShare = useMutation({
    mutationFn: async (input: CreateShareInput) => {
      if (!currentBrandId) throw new Error('No brand selected');

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const shareCode = generateShareCode();

      const { data, error } = await supabase
        .from('os_report_shares')
        .insert({
          brand_id: currentBrandId,
          user_id: userData.user.id,
          share_code: shareCode,
          ...input,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', currentBrandId] });
      toast({
        title: "Success",
        description: "Report share created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create report share",
        variant: "destructive",
      });
      console.error('Error creating report share:', error);
    },
  });

  const updateShare = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ReportShare> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_report_shares')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', currentBrandId] });
      toast({
        title: "Success",
        description: "Report share updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update report share",
        variant: "destructive",
      });
      console.error('Error updating report share:', error);
    },
  });

  const deleteShare = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_report_shares')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', currentBrandId] });
      toast({
        title: "Success",
        description: "Report share deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete report share",
        variant: "destructive",
      });
      console.error('Error deleting report share:', error);
    },
  });

  const createSchedule = useMutation({
    mutationFn: async (input: CreateScheduleInput) => {
      if (!currentBrandId) throw new Error('No brand selected');

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      // Get the share name for the schedule name
      const { data: share } = await supabase
        .from('os_report_shares')
        .select('name')
        .eq('id', input.share_id)
        .single();

      const nextRunAt = calculateNextRunAt(
        input.schedule_type || 'daily',
        input.schedule_time || '09:00',
        input.schedule_days,
        input.schedule_day_of_month
      );

      const { data, error } = await supabase
        .from('job_schedules')
        .insert({
          brand_id: currentBrandId,
          user_id: userData.user.id,
          job_type: 'report_share',
          name: `Report Share: ${share?.name || 'Unknown'}`,
          description: `Scheduled report share delivery`,
          enabled: input.is_enabled ?? true,
          next_run_at: nextRunAt,
          parameters: {
            share_id: input.share_id,
            schedule_type: input.schedule_type || 'daily',
            schedule_time: input.schedule_time || '09:00',
            schedule_days: input.schedule_days || [],
            schedule_day_of_month: input.schedule_day_of_month || null,
            timezone: input.timezone || 'UTC',
            email_subject: input.email_subject || null,
            email_template: input.email_template || null,
          },
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', currentBrandId] });
      queryClient.invalidateQueries({ queryKey: ['job-schedules'] });
      toast({
        title: "Success",
        description: "Schedule created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create schedule",
        variant: "destructive",
      });
      console.error('Error creating schedule:', error);
    },
  });

  const updateSchedule = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ReportShareSchedule> & { id: string }) => {
      // Fetch current schedule to merge parameters
      const { data: currentSchedule } = await supabase
        .from('job_schedules')
        .select('parameters')
        .eq('id', id)
        .single();

      const currentParams = (currentSchedule?.parameters as Record<string, any>) || {};

      const nextRunAt = calculateNextRunAt(
        updates.schedule_type || currentParams.schedule_type || 'daily',
        updates.schedule_time || currentParams.schedule_time || '09:00',
        updates.schedule_days || currentParams.schedule_days,
        updates.schedule_day_of_month || currentParams.schedule_day_of_month
      );

      const { data, error } = await supabase
        .from('job_schedules')
        .update({
          enabled: updates.is_enabled,
          next_run_at: nextRunAt,
          parameters: {
            ...currentParams,
            schedule_type: updates.schedule_type ?? currentParams.schedule_type,
            schedule_time: updates.schedule_time ?? currentParams.schedule_time,
            schedule_days: updates.schedule_days ?? currentParams.schedule_days,
            schedule_day_of_month: updates.schedule_day_of_month ?? currentParams.schedule_day_of_month,
            timezone: updates.timezone ?? currentParams.timezone,
            email_subject: updates.email_subject ?? currentParams.email_subject,
            email_template: updates.email_template ?? currentParams.email_template,
          },
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', currentBrandId] });
      queryClient.invalidateQueries({ queryKey: ['job-schedules'] });
      toast({
        title: "Success",
        description: "Schedule updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update schedule",
        variant: "destructive",
      });
      console.error('Error updating schedule:', error);
    },
  });

  const deleteSchedule = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('job_schedules')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', currentBrandId] });
      queryClient.invalidateQueries({ queryKey: ['job-schedules'] });
      toast({
        title: "Success",
        description: "Schedule deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete schedule",
        variant: "destructive",
      });
      console.error('Error deleting schedule:', error);
    },
  });

  return {
    shares,
    isLoading,
    createShare,
    updateShare,
    deleteShare,
    createSchedule,
    updateSchedule,
    deleteSchedule,
  };
};
